<script setup lang="ts">
import { ScalarButton, ScalarIcon, ScalarListbox } from '@scalar/components'
import { CONTENT_TYPES } from '@scalar/helpers/http/content-types'
import { objectEntries } from '@scalar/helpers/object/object-entries'
import type { ApiReferenceEvents } from '@scalar/workspace-store/events'
import { unpackProxyObject } from '@scalar/workspace-store/helpers/unpack-proxy'
import {
  getExampleFromBody,
  getSelectedBodyContentType,
} from '@scalar/workspace-store/request-example'
import { resolve } from '@scalar/workspace-store/resolve'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import type {
  RequestBodyObject,
  SchemaObject,
} from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'
import { computed } from 'vue'

import { useFileDialog } from '@/hooks/use-file-dialog'
import RequestBodyForm from '@/v2/blocks/request-block/components/RequestBodyForm.vue'
import { getFileName } from '@/v2/blocks/request-block/helpers/files'
import { CodeInput } from '@/v2/components/code-input'
import {
  DataTable,
  DataTableHeader,
  DataTableRow,
} from '@/v2/components/data-table'
import { CollapsibleSection } from '@/v2/components/layout'

const {
  requestBody,
  exampleKey,
  environment,
  requestBodyCompositionSelection,
  title,
} = defineProps<{
  /** Request body */
  requestBody?: RequestBodyObject
  /** Currently selected example key for the current operation */
  exampleKey: string
  /** Display title */
  title: string
  /** Selected environment */
  environment: XScalarEnvironment
  /** Selected anyOf/oneOf request-body variants keyed by schema path */
  requestBodyCompositionSelection?: Record<string, number>
}>()

const emits = defineEmits<{
  (e: 'update:contentType', payload: { value: string }): void
  /** We use this event to update raw values */
  (
    e: 'update:value',
    payload: Pick<
      ApiReferenceEvents['operation:update:requestBody:value'],
      'payload' | 'contentType'
    >,
  ): void
  /** We use this event when updating form data only */
  (
    e: 'update:formValue',
    payload: Pick<
      ApiReferenceEvents['operation:update:requestBody:formValue'],
      'payload' | 'contentType'
    >,
  ): void
}>()

// Map a content type to a language for the code editor
const contentTypeToLanguageMap = {
  'application/json': 'json',
  'application/xml': 'xml',
  'application/yaml': 'yaml',
} as const

/** Selected content type with default */
const selectedContentType = computed(
  () => getSelectedBodyContentType(requestBody, exampleKey) ?? 'none',
)

/** Convert content types to options for the dropdown */
const contentTypeOptions = objectEntries(CONTENT_TYPES).map(([id, label]) => ({
  id,
  label,
}))

const selectedContentTypeModel = computed<{ id: string; label: string }>({
  get: () => {
    const found = contentTypeOptions.find(
      (it) => it.id === selectedContentType.value,
    )
    return found ?? contentTypeOptions.at(-1)!
  },
  set: (v) => {
    emits('update:contentType', { value: v.id })
  },
})

function handleFileUpload(callback: (file: File) => void) {
  const { open } = useFileDialog({
    onChange: (files) => {
      const file = files?.[0]
      if (file) {
        callback(file)
      }
    },
    multiple: false,
    accept: '*/*',
  })
  open()
}

/** Dereferenced example */
const example = computed(
  () =>
    requestBody &&
    getExampleFromBody(
      requestBody,
      selectedContentType.value,
      exampleKey,
      requestBodyCompositionSelection,
    ),
)

/** Convert the example value to a string for the code editor */
const bodyValue = computed(() => {
  if (!example.value) {
    return ''
  }

  const value = example.value.value
  if (typeof value === 'string') {
    return value
  }

  return JSON.stringify(value, null, 2)
})

/** Resolved schema for the request body */
const bodySchema = computed<SchemaObject | undefined>(() => {
  return resolve.schema(
    requestBody?.content?.[selectedContentType.value]?.schema,
  )
})
</script>
<template>
  <CollapsibleSection>
    <template #title>{{ title }}</template>
    <DataTable
      :columns="['']"
      presentational>
      <DataTableHeader
        class="relative col-span-full flex h-8 cursor-pointer items-center justify-between border-r-0 !p-0">
        <ScalarListbox
          v-model="selectedContentTypeModel"
          :options="contentTypeOptions"
          teleport>
          <ScalarButton
            class="text-c-2 hover:text-c-1 flex h-full w-fit gap-1.5 px-3 font-normal"
            fullWidth
            variant="ghost">
            <span>{{
              CONTENT_TYPES[
                selectedContentType as keyof typeof CONTENT_TYPES
              ] ?? selectedContentType
            }}</span>
            <ScalarIcon
              icon="ChevronDown"
              size="md" />
          </ScalarButton>
        </ScalarListbox>
      </DataTableHeader>
      <DataTableRow>
        <!-- No Body -->
        <template v-if="selectedContentType === 'none'">
          <div
            class="text-c-3 flex min-h-10 w-full items-center justify-center border-t p-2 text-sm">
            <span>No Body</span>
          </div>
        </template>

        <!-- Binary File -->
        <template
          v-else-if="selectedContentType === 'application/octet-stream'">
          <div
            class="flex items-center justify-center overflow-hidden border-t p-1.5">
            <template
              v-if="
                getFileName(unpackProxyObject(example?.value)) !== undefined
              ">
              <span
                class="text-c-2 w-full max-w-full overflow-hidden rounded border px-1.5 py-1 text-xs whitespace-nowrap">
                {{ getFileName(unpackProxyObject(example?.value)) }}
              </span>
              <ScalarButton
                class="bg-b-2 hover:bg-b-3 text-c-2 ml-1 border-0 shadow-none"
                size="sm"
                variant="outlined"
                @click="
                  emits('update:value', {
                    payload: undefined,
                    contentType: selectedContentType,
                  })
                ">
                Delete
              </ScalarButton>
            </template>
            <template v-else>
              <ScalarButton
                class="bg-b-2 hover:bg-b-3 text-c-2 border-0 shadow-none"
                size="sm"
                variant="outlined"
                @click="
                  () =>
                    handleFileUpload((file) =>
                      emits('update:value', {
                        payload: file,
                        contentType: selectedContentType,
                      }),
                    )
                ">
                <span>Select File</span>
                <ScalarIcon
                  class="ml-1"
                  icon="Upload"
                  size="xs"
                  thickness="2.5" />
              </ScalarButton>
            </template>
          </div>
        </template>

        <!-- Form Data / URL Encoded -->
        <template
          v-else-if="
            selectedContentType === 'multipart/form-data' ||
            selectedContentType === 'application/x-www-form-urlencoded'
          ">
          <RequestBodyForm
            :bodySchema
            :environment
            :example
            :selectedContentType
            @update:formValue="
              (payload) =>
                emits('update:formValue', {
                  payload,
                  contentType: selectedContentType,
                })
            " />
        </template>

        <!-- Code/Other -->
        <template v-else>
          <CodeInput
            class="border-t px-3"
            content=""
            :environment="environment"
            :language="
              contentTypeToLanguageMap[
                selectedContentType as keyof typeof contentTypeToLanguageMap
              ] ?? 'plaintext'
            "
            lineNumbers
            lint
            :modelValue="bodyValue"
            withFakeData
            @update:modelValue="
              (value) =>
                emits('update:value', {
                  payload: value,
                  contentType: selectedContentType,
                })
            " />
        </template>
      </DataTableRow>
    </DataTable>
  </CollapsibleSection>
</template>
<style scoped>
:deep(.cm-content) {
  font-size: var(--scalar-small);
}
</style>
